import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, X, TrendingUp, TrendingDown, Wallet, RefreshCw, 
  DollarSign, Target, AlertTriangle, History, RotateCcw, Zap 
} from 'lucide-react';
import dayjs from 'dayjs';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SYMBOLS = [
  { symbol: 'BTCUSDT', name: 'Bitcoin' },
  { symbol: 'ETHUSDT', name: 'Ethereum' },
  { symbol: 'SOLUSDT', name: 'Solana' },
  { symbol: 'BNBUSDT', name: 'BNB' }
];

export default function PaperTrading() {
  const [marketMode, setMarketMode] = useState('SPOT'); // SPOT or FUTURES
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [currentPrices, setCurrentPrices] = useState({});
  
  const [newOrder, setNewOrder] = useState({
    symbol: 'BTCUSDT',
    side: 'LONG',
    quantity: 0.01,
    entry_price: 0,
    stop_loss: 0,
    take_profit: 0,
    leverage: 5,
    strategy: 'MANUAL'
  });

  const fetchData = useCallback(async () => {
    try {
      const baseUrl = marketMode === 'FUTURES' ? '/api/futures' : '/api/paper';
      const [accountRes, positionsRes, tradesRes] = await Promise.all([
        axios.get(`${API_URL}${baseUrl}/account`),
        axios.get(`${API_URL}${baseUrl}/positions`),
        axios.get(`${API_URL}${baseUrl}/trades`)
      ]);
      
      setAccount(accountRes.data);
      setPositions(positionsRes.data.positions || []);
      setTrades(tradesRes.data.trades || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching paper trading data');
    } finally {
      setLoading(false);
    }
  }, [marketMode]);

  const fetchPrices = useCallback(async () => {
    try {
      const prices = {};
      for (const s of SYMBOLS) {
        const res = await axios.get(`${API_URL}/api/market/klines-with-indicators/${s.symbol}`, {
          params: { interval: '15m', days: 1 }
        });
        if (res.data?.stats?.price) {
          prices[s.symbol] = res.data.stats.price;
        }
      }
      setCurrentPrices(prices);
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchPrices();
    const interval = setInterval(() => {
      fetchData();
      fetchPrices();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchData, fetchPrices]);

  const handleCreateOrder = async () => {
    if (!newOrder.entry_price || !newOrder.stop_loss || !newOrder.take_profit) {
      toast.error('Please fill all price fields');
      return;
    }

    try {
      const baseUrl = marketMode === 'FUTURES' ? '/api/futures' : '/api/paper';
      await axios.post(`${API_URL}${baseUrl}/positions`, newOrder);
      toast.success('Position opened successfully!');
      setShowOrderForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating position');
    }
  };

  const handleClosePosition = async (positionId, currentPrice) => {
    try {
      const baseUrl = marketMode === 'FUTURES' ? '/api/futures' : '/api/paper';
      await axios.post(`${API_URL}${baseUrl}/positions/${positionId}/close`, null, {
        params: { exit_price: currentPrice }
      });
      toast.success('Position closed!');
      fetchData();
    } catch (error) {
      toast.error('Error closing position');
    }
  };

  const handleResetAccount = async () => {
    if (!window.confirm('Are you sure you want to reset your paper trading account? This will delete all positions and trades.')) {
      return;
    }

    try {
      const baseUrl = marketMode === 'FUTURES' ? '/api/futures' : '/api/paper';
      await axios.post(`${API_URL}${baseUrl}/account/reset`, null, {
        params: { initial_balance: 10000 }
      });
      toast.success('Account reset successfully!');
      fetchData();
    } catch (error) {
      toast.error('Error resetting account');
    }
  };

  // Calculate unrealized PnL
  const calculateUnrealizedPnL = (position) => {
    const currentPrice = currentPrices[position.symbol] || position.entry_price;
    if (position.side === 'LONG') {
      return (currentPrice - position.entry_price) * position.quantity;
    } else {
      return (position.entry_price - currentPrice) * position.quantity;
    }
  };

  const totalUnrealizedPnL = positions.reduce((sum, p) => sum + calculateUnrealizedPnL(p), 0);
  const totalRealizedPnL = trades.reduce((sum, t) => sum + (t.pnl || 0), 0);
  const winningTrades = trades.filter(t => t.pnl > 0).length;
  const losingTrades = trades.filter(t => t.pnl <= 0).length;

  // Update entry price when symbol changes
  useEffect(() => {
    if (currentPrices[newOrder.symbol]) {
      const price = currentPrices[newOrder.symbol];
      setNewOrder(prev => ({
        ...prev,
        entry_price: price,
        stop_loss: prev.side === 'LONG' ? price * 0.98 : price * 1.02,
        take_profit: prev.side === 'LONG' ? price * 1.04 : price * 0.96
      }));
    }
  }, [newOrder.symbol, newOrder.side, currentPrices]);

  return (
    <div>
      <header className="page-header">
        <div className="page-header-content">
          <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Paper Trading</h1>
            
            {/* Market Mode Toggle */}
            <div style={{ display: 'flex', backgroundColor: '#0F1318', borderRadius: '8px', padding: '4px' }}>
              <button
                className={`timeframe-tab ${marketMode === 'SPOT' ? 'active' : ''}`}
                onClick={() => setMarketMode('SPOT')}
                style={{ minWidth: '60px' }}
              >
                Spot
              </button>
              <button
                className={`timeframe-tab ${marketMode === 'FUTURES' ? 'active' : ''}`}
                onClick={() => setMarketMode('FUTURES')}
                style={{ 
                  minWidth: '70px',
                  backgroundColor: marketMode === 'FUTURES' ? '#F59E0B' : undefined 
                }}
              >
                <Zap size={14} style={{ marginRight: '4px' }} />
                Futures
              </button>
            </div>
          </div>
          <div className="header-right">
            <span className="badge" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B' }}>
              {marketMode === 'FUTURES' ? 'Futures Paper' : 'Spot Paper'}
            </span>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={handleResetAccount}
              data-testid="reset-account-button"
            >
              <RotateCcw size={16} /> Reset
            </button>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'loading-spinner' : ''} />
            </button>
          </div>
        </div>
      </header>

      <div className="page-container">
        {/* Account Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">{marketMode === 'FUTURES' ? 'Margin Balance' : 'Balance'}</div>
            <div className="stat-value font-mono" data-testid="account-balance-value">
              ${account?.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '10,000.00'}
            </div>
          </div>
          {marketMode === 'FUTURES' && (
            <div className="stat-card">
              <div className="stat-label">Available</div>
              <div className="stat-value font-mono">
                ${account?.available_balance?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '10,000.00'}
              </div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-label">Unrealized P&L</div>
            <div className={`stat-value font-mono ${totalUnrealizedPnL >= 0 ? 'positive' : 'negative'}`}>
              {totalUnrealizedPnL >= 0 ? '+' : ''}${totalUnrealizedPnL.toFixed(2)}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Realized P&L</div>
            <div className={`stat-value font-mono ${totalRealizedPnL >= 0 ? 'positive' : 'negative'}`}>
              {totalRealizedPnL >= 0 ? '+' : ''}${totalRealizedPnL.toFixed(2)}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Win/Loss</div>
            <div className="stat-value font-mono">
              <span style={{ color: '#22C55E' }}>{winningTrades}</span>
              <span style={{ color: '#9AA3AF' }}> / </span>
              <span style={{ color: '#EF4444' }}>{losingTrades}</span>
            </div>
          </div>
        </div>

        <div className="paper-grid">
          {/* Open Positions */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title">Open Positions</h3>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => setShowOrderForm(true)}
                data-testid="open-position-button"
              >
                <Plus size={16} /> New Position
              </button>
            </div>
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              {positions.length === 0 ? (
                <div className="card-content empty-state">
                  <Target size={40} style={{ color: '#4B5563' }} />
                  <p>No open positions</p>
                </div>
              ) : (
                <table className="data-table" data-testid="positions-table">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Side</th>
                      <th>Qty</th>
                      <th>Entry</th>
                      <th>Current</th>
                      <th>P&L</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((pos) => {
                      const currentPrice = currentPrices[pos.symbol] || pos.entry_price;
                      const pnl = calculateUnrealizedPnL(pos);
                      const pnlPct = ((currentPrice - pos.entry_price) / pos.entry_price) * 100 * (pos.side === 'LONG' ? 1 : -1);

                      return (
                        <tr key={pos._id}>
                          <td style={{ fontWeight: 600 }}>{pos.symbol}</td>
                          <td>
                            <span className={`badge ${pos.side.toLowerCase()}`}>
                              {pos.side === 'LONG' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              {pos.side}
                            </span>
                          </td>
                          <td className="mono">{pos.quantity}</td>
                          <td className="mono">${pos.entry_price?.toFixed(2)}</td>
                          <td className="mono">${currentPrice?.toFixed(2)}</td>
                          <td>
                            <div className={pnl >= 0 ? 'positive' : 'negative'} style={{ color: pnl >= 0 ? '#22C55E' : '#EF4444' }}>
                              <div className="mono">{pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}</div>
                              <div style={{ fontSize: '0.7rem' }}>{pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%</div>
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleClosePosition(pos._id, currentPrice)}
                              data-testid={`close-position-${pos._id}`}
                            >
                              <X size={14} /> Close
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Trade History */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <History size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Trade History
              </h3>
            </div>
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              {trades.length === 0 ? (
                <div className="card-content empty-state">
                  <History size={40} style={{ color: '#4B5563' }} />
                  <p>No trade history</p>
                </div>
              ) : (
                <table className="data-table" data-testid="trades-history-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Symbol</th>
                      <th>Side</th>
                      <th>Entry</th>
                      <th>Exit</th>
                      <th>P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trades.map((trade) => (
                      <tr key={trade._id}>
                        <td className="mono" style={{ fontSize: '0.75rem' }}>
                          {dayjs(trade.created_at).format('MM/DD HH:mm')}
                        </td>
                        <td style={{ fontWeight: 500 }}>{trade.symbol}</td>
                        <td>
                          <span className={`badge ${trade.side?.toLowerCase()}`}>
                            {trade.side}
                          </span>
                        </td>
                        <td className="mono">${trade.entry_price?.toFixed(2)}</td>
                        <td className="mono">${trade.exit_price?.toFixed(2)}</td>
                        <td>
                          <span className={`badge ${trade.pnl >= 0 ? 'win' : 'loss'}`}>
                            {trade.pnl >= 0 ? '+' : ''}${trade.pnl?.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Quick Order Form Modal */}
        {showOrderForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100
          }}>
            <div className="card" style={{ width: '400px', maxWidth: '90%' }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title">
                  {marketMode === 'FUTURES' && <Zap size={16} style={{ marginRight: '8px', color: '#F59E0B' }} />}
                  New {marketMode === 'FUTURES' ? 'Futures' : 'Spot'} Position
                </h3>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowOrderForm(false)}
                >
                  <X size={16} />
                </button>
              </div>
              <div className="card-content">
                <div className="form-group">
                  <label className="form-label">Symbol</label>
                  <select
                    value={newOrder.symbol}
                    onChange={(e) => setNewOrder(prev => ({ ...prev, symbol: e.target.value }))}
                    className="form-select"
                    data-testid="order-symbol-select"
                  >
                    {SYMBOLS.map(s => (
                      <option key={s.symbol} value={s.symbol}>{s.name} ({s.symbol})</option>
                    ))}
                  </select>
                </div>

                {/* Leverage selector for Futures */}
                {marketMode === 'FUTURES' && (
                  <div className="form-group">
                    <label className="form-label">
                      <Zap size={12} style={{ marginRight: '4px' }} />
                      Leverage
                    </label>
                    <select
                      value={newOrder.leverage}
                      onChange={(e) => setNewOrder(prev => ({ ...prev, leverage: parseInt(e.target.value) }))}
                      className="form-select"
                      data-testid="order-leverage-select"
                    >
                      {[1, 2, 3, 5, 10].map(l => (
                        <option key={l} value={l}>{l}x</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Side</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className={`btn ${newOrder.side === 'LONG' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setNewOrder(prev => ({ ...prev, side: 'LONG' }))}
                      style={{ flex: 1, backgroundColor: newOrder.side === 'LONG' ? '#22C55E' : undefined }}
                      data-testid="side-long-button"
                    >
                      <TrendingUp size={16} /> LONG
                    </button>
                    <button
                      className={`btn ${newOrder.side === 'SHORT' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setNewOrder(prev => ({ ...prev, side: 'SHORT' }))}
                      style={{ flex: 1, backgroundColor: newOrder.side === 'SHORT' ? '#EF4444' : undefined }}
                      data-testid="side-short-button"
                    >
                      <TrendingDown size={16} /> SHORT
                    </button>
                  </div>
                </div>

                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      value={newOrder.quantity}
                      onChange={(e) => setNewOrder(prev => ({ ...prev, quantity: parseFloat(e.target.value) }))}
                      className="form-input"
                      step="0.001"
                      min="0.001"
                      data-testid="order-quantity-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Entry Price</label>
                    <input
                      type="number"
                      value={newOrder.entry_price}
                      onChange={(e) => setNewOrder(prev => ({ ...prev, entry_price: parseFloat(e.target.value) }))}
                      className="form-input"
                      step="0.01"
                      data-testid="order-entry-price-input"
                    />
                  </div>
                </div>

                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Stop Loss</label>
                    <input
                      type="number"
                      value={newOrder.stop_loss}
                      onChange={(e) => setNewOrder(prev => ({ ...prev, stop_loss: parseFloat(e.target.value) }))}
                      className="form-input"
                      step="0.01"
                      data-testid="order-stop-loss-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Take Profit</label>
                    <input
                      type="number"
                      value={newOrder.take_profit}
                      onChange={(e) => setNewOrder(prev => ({ ...prev, take_profit: parseFloat(e.target.value) }))}
                      className="form-input"
                      step="0.01"
                      data-testid="order-take-profit-input"
                    />
                  </div>
                </div>

                <div style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: '#0F1318', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span style={{ color: '#9AA3AF' }}>Position Value:</span>
                    <span className="font-mono">${(newOrder.quantity * newOrder.entry_price).toFixed(2)}</span>
                  </div>
                  {marketMode === 'FUTURES' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      <span style={{ color: '#9AA3AF' }}>Margin Required:</span>
                      <span className="font-mono" style={{ color: '#F59E0B' }}>
                        ${((newOrder.quantity * newOrder.entry_price) / newOrder.leverage).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    <span style={{ color: '#9AA3AF' }}>Risk:</span>
                    <span className="font-mono" style={{ color: '#EF4444' }}>
                      ${Math.abs((newOrder.entry_price - newOrder.stop_loss) * newOrder.quantity).toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    <span style={{ color: '#9AA3AF' }}>Reward:</span>
                    <span className="font-mono" style={{ color: '#22C55E' }}>
                      ${Math.abs((newOrder.take_profit - newOrder.entry_price) * newOrder.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleCreateOrder}
                  style={{ width: '100%', marginTop: '1rem' }}
                  data-testid="place-order-button"
                >
                  {marketMode === 'FUTURES' && <Zap size={18} style={{ marginRight: '4px' }} />}
                  <DollarSign size={18} /> Open {marketMode === 'FUTURES' ? `${newOrder.leverage}x ` : ''}Position
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
